import React, { useState, useRef, useEffect, Fragment } from 'react';
import { Checkbox, Form, Input } from '@arco-design/web-react';
import { terminal } from '@/constant/index';
import debug from 'debug';
import { copyToClipboard } from '../../utils/clipboard';

import type { Popup } from '@/types/Form';
import { CARTSEE_TARGET_REDIRECT, EVENTS, STORAGE_KEY } from '@/constant/constant';
import { getLocal, getSession, setLocal } from '@/utils/storage';
import { commonApi, companyUUID, getShopifyUserId } from '@/service/api';
import { isEnableEventCallback } from '@/utils/experiment';
import { isShopifyOrTest } from '@/utils/platform';
import { checkEmailOrPhone, getContactStatus } from '@/service/strategy/request';
import { formEventV2 } from '@/service/formEventV2';
import { reportFormSuccess } from '@/service/formSuccess';
import { PrivacyFieldErrorHint } from '../PrivacyFieldErrorHint';
import { triggerSubscribedCallback, triggerViewedCallback } from '@/utils/popupCallbacks';

const log = debug('cartsee:embedded:popup');

interface EmbeddedPopupProps {
  popupConfig: Popup;
}

const EmbeddedPopup: React.FC<EmbeddedPopupProps> = ({ popupConfig }) => {
  const [form] = Form.useForm();
  const [activePageSetting, setActivePageSetting] = useState('start');
  const popupRef = useRef(null);
  const [hasReported100View, setHasReported100View] = useState(false);
  const [copiedShow, setCopiedShow] = useState(false);
  const [privacyValidateFailed, setPrivacyValidateFailed] = useState(false);
  const [privacyErrorVisibleMap, setPrivacyErrorVisibleMap] = useState<Record<string, boolean | undefined>>({});
  const successReportedRef = useRef(false);

  const { id, name, settings, triggerEventType } = popupConfig;

  // 添加可见性检测
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // 当元素100%可见时
          if (entry.intersectionRatio === 1 && !hasReported100View) {
            // AB 实验映射，埋点上报用
            const info = getLocal(STORAGE_KEY.AB_MAP);
            let abMapping = null;
            if (info) {
              try {
                abMapping = JSON.parse(info);
              } catch (error) {}
            }
            log('embedded popup view step=1 intersectionRatio=%s', entry.intersectionRatio);
            commonApi(
              {
                cid: getLocal(STORAGE_KEY.CID) || null,
                formUid: getLocal(STORAGE_KEY.FORM_UID) || null,
                pathname: window.location.pathname,
                event: 'view',
                companyUUID: companyUUID,
                shopifyUserId: getShopifyUserId(),
                userAgent: navigator.userAgent,
                formId: id,
                step: 1,
                terminal: terminal ? 'pc' : 'mobile',
                abMapping: abMapping
              },
              'form/out/view'
            )
              .then((response) => {})
              .catch((e) => {});

            // 监听曝光的埋点
            formEventV2({
              event: 'popup_view',
              triggerEventType,
              showFrom: getLocal(STORAGE_KEY.SHOW_FROM) || '',
              formId: id,
              formName: name,
              step: 1,
              templateId: popupConfig?.templateId,
              pixel: window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop,
              leaveTriggerType: triggerEventType === 1 ? getSession(STORAGE_KEY.LEAVE_TRIGGER_TYPE) || '' : ''
            })
              .then((response) => {})
              .catch((e) => {});
            triggerViewedCallback({
              popupType: popupConfig?.type,
              popupName: name,
              triggerEventType
            });
            // 设置为已上报100%可见
            setHasReported100View(true);
          }
        });
      },
      { threshold: 1.0 } // 阈值设为1.0表示100%可见时触发
    );

    if (popupRef.current) {
      observer.observe(popupRef.current);
    }

    return () => {
      if (popupRef.current) {
        observer.unobserve(popupRef.current);
      }
    };
  }, [id, name, triggerEventType, hasReported100View, popupConfig.templateId]);

  const handleFormButtonClick = async () => {
    if (activePageSetting === 'start') {
      form
        .validate()
        .then(() => {
          if (settings.rules.buttonAction === 'link') {
            handleSignup();
            // 配置支持原窗口重定向刷新
            settings.rules.openUrl && window.open(settings.rules.openUrl, settings.rules.openUrl.includes(CARTSEE_TARGET_REDIRECT) ? '_self' : 'target');
          } else {
            setPrivacyValidateFailed(false);
            // 提交弹窗
            handleSignup();
            setActivePageSetting('end');
          }
        })
        .catch((error) => {
          setPrivacyValidateFailed(true);
        });
      
    } else {
      if (settings.rules.endButtonAction === 'link') {
        window.open(settings.rules.endOpenUrl, settings.rules.endOpenUrl.includes(CARTSEE_TARGET_REDIRECT) ? '_self' : 'target');
      }
    }
  };

  useEffect(() => {
    if (activePageSetting !== 'end') {
      successReportedRef.current = false;
      return;
    }
    if (successReportedRef.current) {
      return;
    }

    const { email } = form.getFieldsValue();
    reportFormSuccess({
      formId: id,
      email,
      phoneCode: '',
      phone: '',
      step: 1
    }).catch(() => {});

    successReportedRef.current = true;
  }, [activePageSetting, form, id]);

  const handleCouponCopy = async () => {
    const copied = await copyToClipboard(settings.rules.couponCode || '');
    if (copied) {
      setCopiedShow(true);
      setTimeout(() => {
        setCopiedShow(false);
      }, 1000);
    } else {
      log('copy coupon failed');
    }
    // 埋点
    formEventV2({
      event: 'popup_codecopy',
      triggerEventType,
      formId: id,
      formName: name,
      code: settings.rules.couponCode
    })
      .then((response) => {
        // console.log('数据上报成功')
      })
      .catch((e) => {});
  };

  const handleSignup = () => {
    // signup 清除悬浮窗
    localStorage?.removeItem(STORAGE_KEY.TEMP_POP);
    const { email, acceptsMarketing } = form.getFieldsValue();

    log('handleSignup submit email=%s', email);

    formEventV2({
      event: 'popup_submit',
      triggerEventType,
      showFrom: getLocal(STORAGE_KEY.SHOW_FROM) || '',
      formId: id,
      formName: name,
      step: 1,
      templateId: popupConfig?.templateId,
      inputType: 'email',
      email: email
    })
      .then((response) => {
        // console.log('数据上报成功')
      })
      .catch((e) => {});
    
    // 检查邮箱隐私是否展示
    const isEmailPrivacyShow = settings.start?.some(item => item.type === 'Email' && item.enablePrivacyShow === true);

    commonApi(
      {
        cid: getLocal(STORAGE_KEY.CID) || null,
        formUid: getLocal(STORAGE_KEY.FORM_UID) || null,
        event: 'signup',
        companyUUID: companyUUID,
        shopifyUserId: getShopifyUserId(),
        email: email,
        formId: id,
        loginEmail: (window as any)?.cartq?.params?.customer?.email || '',
        phoneCode: '',
        phone: '',
        lastName: '',
        firstName: '',
        birthday: '',
        city: '',
        state: '',
        province: '',
        country: '',
        userAgent: navigator?.userAgent,
        allPropertyList: [],
        ...(isEmailPrivacyShow ? { acceptsMarketing } : {}),
        step: 1,
        pathname: window.location.pathname,
        terminal: terminal ? 'pc' : 'mobile'
      },
      'form/out/signup'
    )
      .then((response) => {
        // 注册成功后写客户信息，供后续接口带参
        setLocal(STORAGE_KEY.CARTX_CUSTOMER, JSON.stringify({ email: email, phone: '' }));
        window?.postMessage('_cartsee_send');

        // 触发配置存储事件
        if (isEnableEventCallback()) {
          window.dispatchEvent(
            new CustomEvent(EVENTS.POPUP.SIGNUP, {
              detail: {
                cid: getLocal(STORAGE_KEY.CID) || null,
                companyUUID: companyUUID,
                shopifyUserId: getShopifyUserId(),
                formId: id,
                event: 'signup',
                email: email,
                phoneCode: '',
                phone: '',
                step: 1
              }
            })
          );
        }

        if (email) {
          triggerSubscribedCallback({
            type: 'email',
            email
          });
        }

        if (response?.data?.data?.cid) {
          setLocal(STORAGE_KEY.CID, response.data.data.cid);
        } else if (response?.data?.data?.formUid) {
          setLocal(STORAGE_KEY.FORM_UID, response.data.data.formUid);
        }

        if (response?.data?.code === 0) {
          if (response?.data?.data?.couponCode) {
            if (isShopifyOrTest()) {
              fetch(window.location.origin + `/discount/${response?.data?.data?.couponCode}`);
            }
          }

          if (isEnableEventCallback() && response?.data?.data?.couponCode) {
            window.dispatchEvent(
              new CustomEvent(EVENTS.POPUP.COUPON, {
                detail: {
                  cid: getLocal(STORAGE_KEY.CID) || null,
                  companyUUID: companyUUID,
                  shopifyUserId: getShopifyUserId(),
                  formId: id,
                  event: 'coupon',
                  couponCode: response?.data?.data?.couponCode,
                  step: 1
                }
              })
            );
          }
        }
      })
      .catch((e) => {});
  };

  const hidePrivateCheckout = true && popupConfig?.type === 'POP_WINDOW';

  const PrivacyBox = ({ item }) => {
    const privacyWrapStyle: React.CSSProperties = {
      marginBottom: 0,
      fontSize: settings.styles.styleMap['cartx-b-privacy']?.['font-size'],
      lineHeight: '16px',
      display: 'inline-block',
      textAlign: 'left',
    };
    const privacyStyle = {
      color: 'inherit',
      fontSize: settings.styles.styleMap['cartx-b-privacy']?.['font-size'],
      lineHeight: '16px',
      textDecoration: 'underline',
    }
    let { text, linkText, privacyPolicyLink, link2Text, privacyPolicyLink2 } = item.type === 'Email' ? item : popupConfig;
    return (
      <span className="cartx-checkbox-set-text" style={privacyWrapStyle}>
        {text}
        <a rel="noreferrer" target="_blank" href={privacyPolicyLink} style={privacyStyle}>
          {linkText}
        </a>
        {link2Text ? (
          <Fragment>
            &nbsp;{item.joinText || 'and'}&nbsp;
            <a rel="noreferrer" target="_blank" href={privacyPolicyLink2} style={privacyStyle}>
              {link2Text}
            </a>
            .
          </Fragment>
        ) : (
          ''
        )}
      </span>
    );
  };

  const PrivacyBlock = () => {
    const currentList = settings.start;
    if (!currentList || currentList.length <= 0) {
      return null;
    }
    return currentList.map((item, index) => {
      const privacyFieldName = item.type === 'Email' ? 'acceptsMarketing' : 'agreePrivacyPolicy';
      const isPrivacyRequired = ['required', 1, '1', true].includes(item?.privacyCheckRequired);
      const privacyValue = form?.getFieldValue(privacyFieldName) || '';
      const privacyChecked = !!privacyValue;
      const explicitError = privacyErrorVisibleMap?.[privacyFieldName];
      const showPrivacyError = isPrivacyRequired && (explicitError ?? (privacyValidateFailed && !privacyChecked));
      if (
        // 邮箱enablePrivacyShow=true为展示
        (item.type === 'Email' && item.enablePrivacyShow !== true) ||
        !item.privacyPolicyLink
      ) {
        return null;
      }
      return (
        <Fragment key={item.type + index}>
          <div className='cartx-privacy-new-wrapper'>
            <div className='cartx-privacy-new-inner'>
              <div
                className={
                  hidePrivateCheckout
                    ? 'cartx-form cartx-privacy-box cartx-privacy-box-new cartx-privacy-no-check-box'
                    : 'cartx-form cartx-privacy-box cartx-privacy-box-new'
                }
                style={{ maxHeight: '43px', overflow: 'auto' }}
                data-position={settings?.rules?.privacyPosition}
              >
                <Form
                  wrapperCol={{ span: 24 }}
                  form={form}
                >
                  <Form.Item
                    field={privacyFieldName}
                    initialValue={(!item.enablePrivacyCheck || item.enablePrivacyCheckValue === 'on') && hidePrivateCheckout ? true : false}
                    className="cartx-option-columns cartx-b-privacy-new"
                    style={{ width: '100%' }}
                    rules={[
                      { validator: (value, callback) => {
                        if (isPrivacyRequired && !value) callback(' ');
                        else callback();
                      } },
                    ]}
                  >
                    {!item.enablePrivacyCheck && hidePrivateCheckout ? (
                      <div>
                        <PrivacyBox item={item} />
                      </div>
                    ) : (
                      <Checkbox
                        defaultChecked={
                          form.getFieldValue(privacyFieldName) !== undefined
                            ? form.getFieldValue(privacyFieldName)
                            : item.enablePrivacyCheck && item.enablePrivacyCheckValue === 'on' && hidePrivateCheckout ? true : false
                        }
                        style={{ marginLeft: 0, marginTop: 0, marginRight: 0, paddingLeft: 0, paddingTop: 0 }}
                        className="cartx-checkbox-set"
                        onChange={(checked: boolean) => {
                          if (!isPrivacyRequired) return;
                          setPrivacyErrorVisibleMap((prev) => ({ ...prev, [privacyFieldName]: !checked }));
                        }}
                      >
                        <PrivacyBox item={item} />
                      </Checkbox>
                    )}
                  </Form.Item>
                </Form>
              </div>
            </div>
          </div>
          {showPrivacyError ? (
            <PrivacyFieldErrorHint
              privacyCheckErrorTip={item?.privacyCheckErrorTip}
              fallback="Please This field is required. email"
            />
          ) : null}
        </Fragment>
      )
    })
  }

  const rules = settings.rules;
  const styleMap = settings.styles.styleMap;

  const textAlignMap: Record<string, string> = {
    'left': 'flex-start',
    'center': 'center',
    'right': 'flex-end',
  };

  return (
    <div
      className="cartx-embedded-popup"
      ref={popupRef}
      id={`cartx-embedded-popup-${id}`}
      style={{
        background: settings.styles.styleMap['cartx-b-show'].background
      }}
    >
      <style>
        {`
          #cartx-embedded-popup-${id} .cartx-embedded-form .cartx-embedded-form-input {
            font-size: 14px;
            border-width: ${settings.rules.inputType === 'bottom' ? '0 0 1px 0' : '1px'} !important;
            border-style: ${settings.rules.inputBorderStyle} !important;
            border-color: ${settings.rules.inputBorderColor} !important;
            border-radius: ${settings.rules.inputBorderRadius} !important;
            background: ${settings.rules.inputBackgroundShow !== 'none' ? settings.rules.inputBackground : 'none'} !important;
            color: ${settings.rules.inputColor} !important;
          }
          #cartx-embedded-popup-${id} .cartx-embedded-form input::-webkit-input-placeholder {
            color: ${settings.rules.inputColor} !important;
            opacity: .3;
          }
          #cartx-embedded-popup-${id} .cartx-embedded-form input:-moz-placeholder {
            color: ${settings.rules.inputColor} !important;
            opacity: .3;
          }
          #cartx-embedded-popup-${id} .cartx-embedded-form input::-moz-placeholder {
            color: ${settings.rules.inputColor} !important;
            opacity: .3;
          }
          #cartx-embedded-popup-${id} .cartx-embedded-form input:-ms-input-placeholder {
            color: ${settings.rules.inputColor} !important;
            opacity: .3;
          }
          #cartx-embedded-popup-${id} .cartx-option-columns{
            text-align: left;
          }
          #cartx-embedded-popup-${id} .cartx-option-columns .arco-radio,
          #cartx-embedded-popup-${id} .cartx-option-columns .arco-checkbox {
            min-width: ${Math.floor(100 / (rules.optionColumns || 1)) - 3}%!important;
            margin-right: 3%!important;
          }
          #cartx-embedded-popup-${id} .cartx-option-columns .arco-radio-text,
          #cartx-embedded-popup-${id} .cartx-option-columns .arco-checkbox-text,
          #cartx-embedded-popup-${id} .cartx-option-columns .cartx-checkbox-set-text {
            color: ${rules.optionColor || '#1D2129'}!important;
          }
          #cartx-embedded-popup-${id} .cartx-option-columns .arco-checkbox-mask,
          #cartx-embedded-popup-${id} .cartx-option-columns .arco-radio-mask{
            border-color: ${rules.optionColor || '#1D2129'}!important;
          }
          #cartx-embedded-popup-${id} .cartx-option-columns .arco-checkbox-checked .arco-checkbox-mask,
          #cartx-embedded-popup-${id} .cartx-option-columns .arco-radio-checked .arco-radio-mask {
            border-color: ${rules.optionBackground || '#1D2129'}!important;
            background: ${rules.optionBackground || '#1D2129'}!important;
          }

          #cartx-embedded-popup-${id} .cartx-privacy-new-wrapper {
            color: ${styleMap['cartx-b-privacy']?.['color']} !important;
            background-color: ${styleMap['cartx-b-privacy']?.['background-color']} !important;
            margin-top: ${styleMap['cartx-b-privacy']?.['margin-top'] || 0}px !important;
            margin-bottom: ${styleMap['cartx-b-privacy']?.['margin-bottom'] || 0 }px  !important;
            margin-left: ${styleMap['cartx-b-privacy']?.['margin-left'] || 0}px  !important;
            margin-right: ${styleMap['cartx-b-privacy']?.['margin-right'] || 0}px  !important;
            padding-top: ${styleMap['cartx-b-privacy']?.['padding-top'] || 5}px !important;
            padding-bottom: ${styleMap['cartx-b-privacy']?.['padding-bottom'] || 5}px  !important;
            padding-left: ${styleMap['cartx-b-privacy']?.['padding-left'] || 10}px  !important;
            padding-right: ${styleMap['cartx-b-privacy']?.['padding-right'] || 10}px  !important;
            min-width: unset !important;
            border-radius: 4px;
            display: flex;
            flex-direction: column;
            box-sizing: border-box;
            align-items: ${textAlignMap[styleMap['cartx-b-privacy']?.['text-align'] || 'left']};
          }
          #cartx-embedded-popup-${id} .cartx-privacy-new-wrapper .cartx-privacy-new-inner {
            box-sizing: border-box;
          }
          #cartx-embedded-popup-${id} .cartx-privacy-box.cartx-privacy-box-new {
            border-radius: 0;
            padding: 0;
            background-color: unset;
            margin: 0;
          }
          #cartx-embedded-popup-${id} .cartx-privacy-box.cartx-privacy-box-new .arco-form-item-control {
            min-height: 26px;
          }
          #cartx-embedded-popup-${id} .cartx-option-columns.cartx-b-privacy-new .cartx-checkbox-set .arco-checkbox-mask-wrapper {
            margin-left: 0;
          }
          #cartx-embedded-popup-${id} .cartx-option-columns.cartx-b-privacy-new .arco-radio-text,
          #cartx-embedded-popup-${id} .cartx-option-columns.cartx-b-privacy-new .arco-checkbox-text,
          #cartx-embedded-popup-${id} .cartx-option-columns.cartx-b-privacy-new .cartx-checkbox-set-text {
            color: ${styleMap['cartx-b-privacy']?.color}!important;
            font-family: ${styleMap['cartx-b-privacy']?.['font-family']} !important;
            padding-top: 1px;
            padding-bottom: 1px;
          }
          #cartx-embedded-popup-${id} .cartx-option-columns.cartx-b-privacy-new .arco-checkbox-mask,
          #cartx-embedded-popup-${id} .cartx-option-columns.cartx-b-privacy-new .arco-radio-mask {
            border-width: 1px;
            border-color: ${styleMap['cartx-b-privacy']?.color}!important;
          }
          #cartx-embedded-popup-${id} .cartx-option-columns.cartx-b-privacy-new .arco-checkbox-checked .arco-checkbox-mask,
          #cartx-embedded-popup-${id} .cartx-option-columns.cartx-b-privacy-new .arco-radio-checked .arco-radio-mask {
            border-color: ${styleMap['cartx-b-privacy-check']?.color} !important;
            background: ${styleMap['cartx-b-privacy-check']?.color} !important;
          }
          #cartx-embedded-popup-${id} .cartx-option-columns.cartx-b-privacy-new .arco-radio,
          #cartx-embedded-popup-${id} .cartx-option-columns.cartx-b-privacy-new .arco-checkbox {
            min-width: unset !important;
            margin-right: 0 !important;
          }
        `}
      </style>
      <div className="cartx-a-message-copy cartx-b-message-copy" style={{ display: copiedShow ? 'block' : 'none' }}>
        copied
      </div>
      <div className="cartx-embedded-content">
        {((activePageSetting === 'start' && settings.texts.topTitle !== '') || (activePageSetting !== 'start' && settings.texts.endTopTitle !== '')) && (
          <div
            className="cartx-embedded-title"
            style={{
              color: activePageSetting === 'start' ? settings.styles.styleMap['cartx-b-title'].color : settings.styles.styleMap['cartx-b-title-end'].color,
              fontFamily: activePageSetting === 'start' ? settings.styles.styleMap['cartx-b-title']['font-family'] : settings.styles.styleMap['cartx-b-title-end']['font-family'],
              fontSize: activePageSetting === 'start' ? settings.styles.styleMap['cartx-b-title']['font-size'] : settings.styles.styleMap['cartx-b-title-end']['font-size'],
              textAlign: activePageSetting === 'start' ? settings.styles.styleMap['cartx-b-title']['text-align'] : settings.styles.styleMap['cartx-b-title-end']['text-align']
            }}
          >
            {activePageSetting === 'start' ? settings.texts.topTitle : settings.texts.endTopTitle}
          </div>
        )}

        {((activePageSetting === 'start' && settings.texts.topSubTitle !== '') || (activePageSetting !== 'start' && settings.texts.endTopSubTitle !== '')) && (
          <div
            className="cartx-embedded-subtitle"
            style={{
              color: activePageSetting === 'start' ? settings.styles.styleMap['cartx-b-subtitle'].color : settings.styles.styleMap['cartx-b-subtitle-end'].color,
              fontFamily: activePageSetting === 'start' ? settings.styles.styleMap['cartx-b-subtitle']['font-family'] : settings.styles.styleMap['cartx-b-subtitle-end']['font-family'],
              fontSize: activePageSetting === 'start' ? settings.styles.styleMap['cartx-b-subtitle']['font-size'] : settings.styles.styleMap['cartx-b-subtitle-end']['font-size'],
              textAlign: activePageSetting === 'start' ? settings.styles.styleMap['cartx-b-subtitle']['text-align'] : settings.styles.styleMap['cartx-b-subtitle-end']['text-align']
            }}
          >
            {activePageSetting === 'start' ? settings.texts.topSubTitle : settings.texts.endTopSubTitle}
          </div>
        )}

        {activePageSetting === 'start' && (
          <div className="cartx-embedded-form">
            <Form form={form} layout="vertical">
              {settings.start.map((item, index) => {
                if (item.routine && item.type === 'Email') {
                  return (
                    <div className="cartx-form-wrapper" key={'form' + index}>
                      <Form.Item
                        style={{
                          marginBottom: 0
                        }}
                        label=""
                        field="email"
                        key={index}
                        validateTrigger={'onSubmit'}
                        rules={[
                          { required: item.necessary, message: popupConfig.tipEmptyEmailText ?? 'Please enter email' },
                          { maxLength: 255, message: popupConfig.tipInvalidEmailText ?? "Please don't exceed 255" },
                          {
                            validator: (val, cb) => {
                              if (item.necessary) {
                                let reg = new RegExp("^[\\w!#$%&'*+/=?^_`{|}~-]+(?:\\.[\\w!#$%&'*+/=?^_`{|}~-]+)*@(?:[\\w](?:[\\w-]*[\\w])?\\.)+[\\w](?:[\\w-]*[\\w])?$");
                                if (!reg.test(val)) {
                                  cb(popupConfig.tipInvalidEmailText ?? 'Please enter a valid email');
                                }
                              }
                              cb();
                            }
                          },
                          ...(item.formSubscribeSignup
                            ? [
                                {
                                  validator: async (value, callback) => {
                                    try {
                                      const res: any = await getContactStatus({
                                        companyUUID,
                                        type: 'email',
                                        email: value,
                                        phone: null,
                                        validate: true
                                      });
                                      if (res?.data?.code === 0 && res?.data?.data?.subscribeStatus === 1) {
                                        callback(item.tipSubscribeEmailText ?? 'You are already subscribed.');
                                      }
                                    } catch (error) {
                                      callback('subscribe check error');
                                    }
                                  }
                                }
                              ]
                            : []
                          ),
                          ...(item.formRepeatSignup
                            ? [
                                {
                                  validator: async (value, callback) => {
                                    try {
                                      const res: any = await checkEmailOrPhone({
                                        phone: null,
                                        companyUUID,
                                        formId: id,
                                        email: value
                                      });
                                      if (res?.data?.code !== 0) {
                                        callback(res?.data?.message);
                                      }
                                    } catch (error) {
                                      callback('email check error');
                                    }
                                  }
                                }
                              ]
                            : []
                          )
                        ]}
                      >
                        <Input
                          className={'cartx-embedded-form-input'}
                          onFocus={(e) => {
                            console.log('onFocus', e.target.value);
                            //数据上报
                            formEventV2({
                              event: 'popup_start_typing',
                              triggerEventType,
                              formId: id,
                              formName: name,
                              step: 0,
                              templateId: popupConfig.templateId,
                              inputType: item.type
                            })
                              .then((response) => {
                                // console.log('数据上报成功')
                              })
                              .catch((e) => {});
                          }}
                          onBlur={(e) => {
                            console.log('onBlur', e.target.value);
                            formEventV2({
                              event: 'popup_stop_typing',
                              triggerEventType,
                              formId: id,
                              formName: name,
                              step: 1,
                              templateId: popupConfig?.templateId,
                              inputType: item.type,
                              remark: e.target.value
                            })
                              .then((response) => {
                                // console.log('数据上报成功')
                              })
                              .catch((e) => {});
                          }}
                          placeholder={item.placeholder}
                        />
                      </Form.Item>
                    </div>
                  );
                }
                return null;
              })}
            </Form>
            {/* 主按钮 */}
            <div
              onClick={handleFormButtonClick}
              className="cartx-embedded-form-button"
              style={{
                color: activePageSetting === 'start' ? settings.styles.styleMap['cartx-b-button'].color : settings.styles.styleMap['cartx-b-button-end'].color,
                fontFamily: activePageSetting === 'start' ? settings.styles.styleMap['cartx-b-button']['font-family'] : settings.styles.styleMap['cartx-b-button-end']['font-family'],
                background:
                  activePageSetting === 'start'
                    ? settings.styles.styleMap['cartx-b-show .cartx-b-button']?.['background'] || settings.styles.styleMap['cartx-b-button']['background']
                    : settings.styles.styleMap['cartx-b-show .cartx-b-button-end']?.['background'] || settings.styles.styleMap['cartx-b-button-end']['background'],
                borderStyle: activePageSetting === 'start' ? settings.styles.styleMap['cartx-b-button']['border-style'] : settings.styles.styleMap['cartx-b-button-end']['border-style'],
                borderColor: activePageSetting === 'start' ? settings.styles.styleMap['cartx-b-button']['border-color'] : settings.styles.styleMap['cartx-b-button-end']['border-color'],
                borderRadius: activePageSetting === 'start' ? settings.styles.styleMap['cartx-b-button']['border-radius'] : settings.styles.styleMap['cartx-b-button-end']['border-radius']
              }}
            >
              {activePageSetting === 'start' ? settings.texts.mainButton : settings.texts.endMainButton}
            </div>
          </div>
        )}

        {activePageSetting === 'start' && ['form', 'button'].includes(settings?.rules?.privacyPosition) && (
          <PrivacyBlock />
        )}

        {settings.rules.couponCodeShow && activePageSetting === settings.rules.couponCodePosition && settings.styles.styleMap.hasOwnProperty('cartx-b-coupon-prompt') && (
          <div
            className="cartx-embedded-coupon-prompt"
            style={{
              color: settings.styles.styleMap['cartx-b-coupon-prompt'].color,
              fontFamily: settings.styles.styleMap['cartx-b-coupon-prompt']['font-family'],
              fontSize: settings.styles.styleMap['cartx-b-coupon-prompt']['font-size'],
              textAlign: settings.styles.styleMap['cartx-b-coupon-prompt']['text-align']
            }}
          >
            {settings.texts.couponPrompt}
          </div>
        )}

        {settings.rules.couponCodeShow && activePageSetting === settings.rules.couponCodePosition && (
          <div
            className={settings.rules.couponCodeType ? `cartx-embedded-coupon-${settings.rules.couponCodeType}` : ''}
            style={{
              color: settings.styles.styleMap[`cartx-b-complete-${settings.rules.couponCodeType}`].color,
              borderRadius: settings.styles.styleMap[`cartx-b-complete-${settings.rules.couponCodeType}`]['border-radius'],
              borderColor: settings.styles.styleMap[`cartx-b-complete-${settings.rules.couponCodeType}`]['border-color'],
              background:
                settings.styles.styleMap[`cartx-b-show .cartx-b-complete-${settings.rules.couponCodeType}`]?.background ||
                settings.styles.styleMap[`cartx-b-complete-${settings.rules.couponCodeType}`].background
            }}
          >
            <div
              className={settings.rules.couponCodeType ? `cartx-embedded-coupon-text-${settings.rules.couponCodeType}` : ''}
              style={{
                borderRadius: settings.rules.couponCodeType === '1' ? settings.styles.styleMap['cartx-b-complete-text-1']['border-radius'] : '',
                color: settings.styles.styleMap[`cartx-b-complete-${settings.rules.couponCodeType}`].color
              }}
            >
              {settings.rules.couponCode}
            </div>
            <div
              className={settings.rules.couponCodeType ? `cartx-embedded-coupon-copy-${settings.rules.couponCodeType}` : ''}
              style={{ color: settings.styles.styleMap[`cartx-b-complete-${settings.rules.couponCodeType}`].color }}
              onClick={handleCouponCopy}
            >
              {popupConfig.couponCopyText}
            </div>
            <div className={settings.rules.couponCodeType ? `cartx-embedded-coupon-icon-${settings.rules.couponCodeType}` : ''} onClick={handleCouponCopy}>
              <svg className="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" width="40" height="40">
                <path d="M274.285714 274.285714v-190.171428A47.542857 47.542857 0 0 1 321.828571 36.571429h618.057143a47.542857 47.542857 0 0 1 47.542857 47.542857V702.171429a47.542857 47.542857 0 0 1-47.542857 47.542857h-190.171428v189.805714a47.835429 47.835429 0 0 1-47.908572 47.908571h-617.325714a47.835429 47.835429 0 0 1-47.908571-47.908571l0.146285-617.325714c0-26.477714 21.357714-47.908571 47.908572-47.908572H274.285714z m87.332572-1.243428h341.577143a46.811429 46.811429 0 0 1 46.811428 46.811428v337.261715h145.042286V128.585143H361.618286v144.457143z m-238.153143 87.04v540.525714H662.674286v-540.525714H123.465143z"></path>
              </svg>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmbeddedPopup;
